// Team-shortcuts API client — carved verbatim from `src/lib/api/team-shortcuts.ts`
// (which now re-exports from here) as the org-teams D1 residue (ARCFE-1041).
// It was the transport blocker under the 3 `useMutateTeamShortcut` hooks, which
// stay app-side as D2 work with their own blockers.
//
// Transport comes from the `@heidi/api/client` registry (E5, ARCFE-805), so
// `http()` returns the app's real `scribeHttpV2` singleton and request
// behaviour is byte-identical to the legacy `scribeHttpV2` import.
//
// UNLIKE the sibling `teams.ts` / `organization.ts` cohorts, every fn here
// PROJECTS the transport response rather than handing it back verbatim: three
// unwrap `.data` (and `list` unwraps `.data.shortcuts` on top) and `delete`
// discards it entirely. That is the frozen contract its callers read — the
// parity table in the co-located test pins each projection, so "make the
// module consistent with its siblings" fails there rather than in production.
//
// Base path: /api/v2/ml-scribe/teams/{team_id}/shortcuts

import { getScribeHttpClients } from '@heidi/api/client';
import {
  type CreateTeamShortcutRequest,
  type TeamShortcut,
  type TeamShortcutListResponse,
  type UpdateTeamShortcutRequest,
} from '../../contracts';
import { type WorkspaceHttpClient } from '../config';

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

const base = (teamId: string) => `/teams/${teamId}/shortcuts`;

/** @domain workspace */
export async function listTeamShortcuts(teamId: string): Promise<TeamShortcut[]> {
  const { data } = await http().get<TeamShortcutListResponse>(base(teamId));
  return data.shortcuts;
}

/** @domain workspace */
export async function createTeamShortcut(
  teamId: string,
  body: CreateTeamShortcutRequest
): Promise<TeamShortcut> {
  const { data } = await http().post<TeamShortcut>(base(teamId), body);
  return data;
}

/** @domain workspace */
export async function updateTeamShortcut(
  teamId: string,
  shortcutId: string,
  patch: UpdateTeamShortcutRequest
): Promise<TeamShortcut> {
  const { data } = await http().patch<TeamShortcut>(
    `${base(teamId)}/${shortcutId}`,
    patch
  );
  return data;
}

/** @domain workspace */
export async function deleteTeamShortcut(
  teamId: string,
  shortcutId: string
): Promise<void> {
  await http().delete(`${base(teamId)}/${shortcutId}`);
}

export const teamShortcutsApi = {
  list: listTeamShortcuts,
  create: createTeamShortcut,
  update: updateTeamShortcut,
  delete: deleteTeamShortcut,
};
